import { Button, Stack, Typography } from '@mui/material'
import { PageContainerStack } from './DataAndPrivacy'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import { COLORS, STRIPE } from 'app-constants'
import { authenticatedUserAtom } from 'state'
import { useRecoilValue } from 'recoil'
import { Subscription, SubscriptionStatus } from 'api/model'
import { useActiveOrganization } from 'hooks/use-organization'
import { capitalizeOnlyFirstLetter, formatTimestamp } from 'utils'

// TODO: move this out to its own component file
export const WarningBanner = ({
  title,
  buttonText,
  onButtonClick,
}: {
  title: string
  buttonText: string
  onButtonClick: () => void
}) => {
  return (
    <Stack
      p="20px"
      borderRadius="8px"
      direction="row"
      alignItems="center"
      sx={{
        background: COLORS.ORANGE_10_PERCENT,
        border: `1px solid ${COLORS.ORANGE}`,
      }}>
      <WarningRoundedIcon
        sx={{
          color: COLORS.ORANGE,
        }}
      />
      <Typography
        variant="pS"
        sx={{
          mx: '20px',
          width: '100%',
        }}>
        {title}
      </Typography>

      <Button
        variant="text"
        onClick={onButtonClick}
        sx={{
          color: COLORS.BROWN,
        }}>
        {buttonText}
      </Button>
    </Stack>
  )
}

enum WarningType {
  paymentFailed = 'payment_failed',
  cardExpiringSoon = 'card_expiring_soon',
  cancelAtPeriodEnd = 'cancel_at_period_end',
}

const BillingWarningBanner = ({ warningType }: { warningType: string }) => {
  const user = useRecoilValue(authenticatedUserAtom)
  const getBannerText = () => {
    switch (warningType) {
      case WarningType.paymentFailed:
        return 'Something went wrong with processing your payment. Please review your payment information or try a different credit card.'
      case WarningType.cardExpiringSoon:
        return 'Your credit card is expiring soon. Please update your payment information.'
      case WarningType.cancelAtPeriodEnd:
        return 'Your subscription will be cancelled at the end of the current period. Please renew your subscription to continue using Jelled.'
    }
    return ''
  }

  const getButtonText = () => {
    switch (warningType) {
      case WarningType.paymentFailed:
        return 'Review'
      case WarningType.cardExpiringSoon:
        return 'Update'
      case WarningType.cancelAtPeriodEnd:
        return 'Renew'
    }
    return ''
  }

  if (!warningType) {
    return null
  }

  return (
    <WarningBanner
      title={getBannerText()}
      buttonText={getButtonText()}
      onButtonClick={() => {
        window.open(user?.subscription?.stripe_management_url || STRIPE.CUSTOMER_PORTAL)
      }}
    />
  )
}

const SubscriptionInfoBasic = ({ subscriptionType }: { subscriptionType: 'enterprise' | 'eternal' | 'beta' }) => {
  const isMobile = useCheckMobileScreen()
  const getHeaderText = () => {
    return capitalizeOnlyFirstLetter(subscriptionType) + ' subscription'
  }

  const getSubtitleText = () => {
    return `You are currently part of a ${subscriptionType} subscription. Please reach out to support@jelled.ai for any questions.`
  }

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      alignItems="center"
      justifyContent="space-between"
      borderRadius="8px"
      mt={3}
      sx={{
        p: '20px',
        background: COLORS.PURE_WHITE,
      }}>
      <Stack mb={isMobile ? 2 : 0} spacing={1}>
        <Typography variant="h5">{getHeaderText()}</Typography>
        <Typography variant="pS">{getSubtitleText()}</Typography>
      </Stack>
    </Stack>
  )
}

const SubscriptionInfoDetailed = ({ subscription }: { subscription: Subscription }) => {
  const [activeOrganization] = useActiveOrganization()
  const isMobile = useCheckMobileScreen()
  const user = useRecoilValue(authenticatedUserAtom)

  const orgSubscription = user?.profiles.find((p) => p.organization.id === activeOrganization)?.organization
    .subscription
  const isEternalSubscription = orgSubscription?.product_name === 'eternal'

  const getHeaderText = () => {
    if (isEternalSubscription) {
      return 'Enterprise subscription'
    }

    switch (subscription.status) {
      case SubscriptionStatus.active:
        return 'Monthly subscription'
      case SubscriptionStatus.canceled:
      case SubscriptionStatus.expired:
        return 'Subscription cancelled'
    }
    return ''
  }

  const getSubtitleText = () => {
    if (isEternalSubscription) {
      return 'You are currently part of an enterprise subscription. Please reach out to support@jelled.ai for any questions.'
    }

    switch (subscription.status) {
      case SubscriptionStatus.active:
        return `Your next recurring payment is on ${
          subscription.next_payment_date
            ? formatTimestamp(subscription.next_payment_date)
            : formatTimestamp(subscription.date_expires)
        }.`
      case SubscriptionStatus.canceled:
        return `Your subscription has been cancelled.`
      case SubscriptionStatus.expired:
        return `Your subscription expired on ${formatTimestamp(subscription.date_expires)}.`
    }
    return ''
  }

  const getButton = () => {
    if (isEternalSubscription) {
      return null
    }

    switch (subscription.status) {
      case SubscriptionStatus.active:
      case SubscriptionStatus.canceled:
      case SubscriptionStatus.expired:
        return (
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              window.open(user?.subscription?.stripe_management_url || STRIPE.CUSTOMER_PORTAL)
            }}
            sx={{
              width: 'fit-content',
            }}>
            Manage plan
          </Button>
        )
    }
  }

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      alignItems="center"
      justifyContent="space-between"
      borderRadius="8px"
      mt={3}
      sx={{
        p: '20px',
        background: COLORS.PURE_WHITE,
      }}>
      <Stack mb={isMobile ? 2 : 0} spacing={1}>
        <Typography variant="h5">{getHeaderText()}</Typography>
        <Typography variant="pS">{getSubtitleText()}</Typography>
      </Stack>

      {getButton()}
    </Stack>
  )
}

const Billing = () => {
  const isMobile = useCheckMobileScreen()
  const [activeOrganization] = useActiveOrganization()

  const user = useRecoilValue(authenticatedUserAtom)

  const orgSubscription = user?.profiles.find((p) => p.organization.id === activeOrganization)?.organization
    .subscription
  const isEnterpriseSubscription = orgSubscription?.product_name === 'enterprise'
  const isEternalSubscription = orgSubscription?.product_name === 'eternal'
  const isBetaSubscription = orgSubscription?.product_name === 'beta'

  const userSubscription = user?.subscription

  const renderContent = () => {
    if (userSubscription === undefined) {
      if (isEternalSubscription) {
        return <SubscriptionInfoBasic subscriptionType="eternal" />
      } else if (isEnterpriseSubscription) {
        return <SubscriptionInfoBasic subscriptionType="enterprise" />
      } else if (isBetaSubscription) {
        return <SubscriptionInfoBasic subscriptionType="beta" />
      }
    }

    if (userSubscription) {
      return (
        <Stack spacing={2}>
          {userSubscription.warnings.map((warning, index) => {
            return <BillingWarningBanner key={index} warningType={warning} />
          })}
          <SubscriptionInfoDetailed subscription={userSubscription} />
        </Stack>
      )
    }

    return null
  }

  return (
    <PageContainerStack>
      {!isMobile && (
        <Typography variant="h4" mb={3}>
          Billing
        </Typography>
      )}
      {renderContent()}
    </PageContainerStack>
  )
}

export default Billing
